import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Building2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const emptyForm = {
  name: "",
  contact_person: "",
  email: "",
  phone: "",
  address: "",
  website: "",
  tax_id: "",
  payment_terms: "",
  notes: "",
};

export default function Suppliers() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () => api.listSuppliers(),
  });

  const saveMut = useMutation({
    mutationFn: (body) =>
      editing ? api.updateSupplier(editing.id, body) : api.createSupplier(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      toast.success(editing ? "Supplier updated" : "Supplier added");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteSupplier(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      setConfirmDeleteId(null);
      toast.success("Supplier removed");
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "",
      contact_person: s.contact_person || "",
      email: s.email || "",
      phone: s.phone || "",
      address: s.address || "",
      website: s.website || "",
      tax_id: s.tax_id || "",
      payment_terms: s.payment_terms || "",
      notes: s.notes || "",
    });
    setOpen(true);
  };

  const submit = () => {
    if (!form.name.trim()) {
      toast.error("Supplier name is required");
      return;
    }
    saveMut.mutate(form);
  };

  return (
    <div data-testid="suppliers-page">
      <PageHeader
        overline="procurement"
        title="Suppliers"
        actions={
          <Button
            data-testid="supplier-new-btn"
            onClick={openCreate}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="size-4 mr-1.5" /> Add supplier
          </Button>
        }
      />

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : suppliers.length === 0 ? (
        <EmptyState
          title="No suppliers yet"
          hint="Add a supplier to issue Purchase Orders"
          action={
            <Button
              data-testid="supplier-empty-new-btn"
              onClick={openCreate}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="size-4 mr-1.5" /> Add supplier
            </Button>
          }
        />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">Supplier</TableHead>
                <TableHead className="overline">Contact</TableHead>
                <TableHead className="overline">Email</TableHead>
                <TableHead className="overline">Phone</TableHead>
                <TableHead className="overline">Payment terms</TableHead>
                <TableHead className="overline text-right w-24">&nbsp;</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {suppliers.map((s) => (
                <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      <Building2 className="size-3.5 text-primary" />
                      {s.name}
                    </span>
                    {s.website && (
                      <div className="text-[10px] text-muted-foreground mono ml-5">
                        {s.website}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {s.contact_person || "—"}
                  </TableCell>
                  <TableCell className="text-xs">
                    {s.email ? (
                      <span className="inline-flex items-center gap-1 mono">
                        <Mail className="size-3" /> {s.email}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs mono">
                    {s.phone ? (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="size-3" /> {s.phone}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {s.payment_terms || (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        data-testid={`supplier-edit-${s.id}`}
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => openEdit(s)}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`supplier-delete-${s.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                        onClick={() => setConfirmDeleteId(s.id)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { if (!v) { setEditing(null); setForm(emptyForm); } setOpen(v); }}>
        <DialogContent data-testid="supplier-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit supplier" : "Add supplier"}</DialogTitle>
            <DialogDescription className="text-xs">
              Suppliers are used to issue Purchase Orders when replenishing inventory.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">Supplier name *</Label>
              <Input
                data-testid="supplier-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Grand IT Distribution"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Contact person</Label>
                <Input
                  data-testid="supplier-form-contact"
                  value={form.contact_person}
                  onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                />
              </div>
              <div>
                <Label className="overline">Email</Label>
                <Input
                  data-testid="supplier-form-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Phone</Label>
                <Input
                  data-testid="supplier-form-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <Label className="overline">Website</Label>
                <Input
                  data-testid="supplier-form-website"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label className="overline">Address</Label>
              <Input
                data-testid="supplier-form-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Tax ID</Label>
                <Input
                  data-testid="supplier-form-tax"
                  value={form.tax_id}
                  onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                />
              </div>
              <div>
                <Label className="overline">Payment terms</Label>
                <Input
                  data-testid="supplier-form-terms"
                  placeholder="e.g. Net 30"
                  value={form.payment_terms}
                  onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label className="overline">Notes</Label>
              <Textarea
                data-testid="supplier-form-notes"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="supplier-form-cancel">
              Cancel
            </Button>
            <Button
              data-testid="supplier-form-save"
              disabled={saveMut.isPending}
              className="bg-primary hover:bg-primary/90"
              onClick={submit}
            >
              {saveMut.isPending ? "Saving…" : editing ? "Save changes" : "Add supplier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDeleteId} onOpenChange={(v) => !v && setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this supplier?</AlertDialogTitle>
            <AlertDialogDescription>
              This can&apos;t be undone. Suppliers referenced by any purchase order can&apos;t be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="supplier-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="supplier-delete-confirm"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteMut.mutate(confirmDeleteId)}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
